/**
 * PATH: packages/ui/src/components/global-search/frecency.ts
 * FILE_ID: UI-GLOBALSEARCH-FRECENCY-001
 * PURPOSE: Frecency (frequency × recency) store + ranking boost for the
 *          global-search palette — rows the operator repeatedly picks float
 *          up WITHIN their section; section order itself never changes.
 * WHY: Search-architecture plan W2 (2026-07-07). Industry-standard palette
 *      behaviour (VS Code, Slack, Raycast, Firefox awesome-bar): what you
 *      picked yesterday is what you almost certainly want today. Before
 *      this, result order inside a section was pure arrival order, so the
 *      job an operator opens every morning ranked below never-touched rows
 *      that happened to arrive earlier.
 * ALGORITHM: score = count × 0.5^(ageDays / 7) — a 7-day half-life, simple
 *      enough to reason about, matches Firefox's decay family. Untouched
 *      rows score 0, so a session with no history ranks byte-identically to
 *      the pre-frecency order (stable sort ties fall back to arrival index).
 * FLOW:
 *   selection (click/Enter) → recordFrecency(key) → localStorage map
 *   final merge → frecencyScoreOf(key) → within-section tiebreak in useSearch
 * DEPENDENCIES: none (localStorage guarded — SSR/test safe).
 * RELATED:
 *  - useSearch.ts (ranking + record on Enter), SearchResults.tsx (record on
 *    click), telemetry.ts (sibling seam, same selection sites)
 */

export interface FrecencyEntry {
  /** Times this result was selected. */
  count: number
  /** Epoch ms of the most recent selection. */
  last: number
}

export type FrecencyMap = Record<string, FrecencyEntry>

const STORAGE_KEY = 'quotech-search-frecency'
/** Cap on tracked results — beyond this the lowest-scoring entries are
 *  evicted so localStorage cannot grow unbounded. */
const MAX_ENTRIES = 200
const HALF_LIFE_DAYS = 7
const DAY_MS = 86_400_000

/** Identity key for a result — same shape as useSearch's dedupe key so
 *  the frecency of a row survives re-fetches. Pure I→O. */
export function frecencyKey(result: {
  providerId?: string
  type: string
  id: string | number
}): string {
  return `${result.providerId || result.type}-${result.id}`
}

/** Decayed score of one entry at `now`. Pure I→O. */
export function frecencyScore(
  entry: FrecencyEntry | undefined,
  now: number,
): number {
  if (!entry || entry.count <= 0) return 0
  const ageDays = Math.max(0, now - entry.last) / DAY_MS
  return entry.count * Math.pow(0.5, ageDays / HALF_LIFE_DAYS)
}

/** Record a selection into the map (returns a NEW map — pure I→O).
 *  Evicts the lowest-scoring entries past MAX_ENTRIES. */
export function recordSelection(
  map: FrecencyMap,
  key: string,
  now: number,
): FrecencyMap {
  const prev = map[key]
  const next: FrecencyMap = {
    ...map,
    [key]: { count: (prev?.count ?? 0) + 1, last: now },
  }
  const keys = Object.keys(next)
  if (keys.length > MAX_ENTRIES) {
    keys
      .sort((a, b) => frecencyScore(next[a], now) - frecencyScore(next[b], now))
      .slice(0, keys.length - MAX_ENTRIES)
      .forEach((k) => delete next[k])
  }
  return next
}

/** Load the persisted map. Corrupt/absent storage → empty map (search must
 *  never break on a bad cache). */
export function loadFrecency(): FrecencyMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: FrecencyMap = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const e = v as Partial<FrecencyEntry> | null
      if (e && typeof e.count === 'number' && typeof e.last === 'number') {
        out[k] = { count: e.count, last: e.last }
      }
    }
    return out
  } catch {
    return {}
  }
}

/** Persist the map. Failures (quota, private mode) are swallowed. */
export function saveFrecency(map: FrecencyMap): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* frecency is an enhancement, never a failure mode */
  }
}

/** Convenience one-shot used at the two selection sites: load → record →
 *  save. Kept tiny + synchronous so the click handler stays snappy. */
export function recordFrecencySelection(result: {
  providerId?: string
  type: string
  id: string | number
}): void {
  const now = Date.now()
  saveFrecency(recordSelection(loadFrecency(), frecencyKey(result), now))
}
